import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, Search, Plus, Loader2, Minus, History } from 'lucide-react';
import { loyaltyService, LoyaltyCustomer } from '@/services/loyalty';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { formatPrice, formatDateTime } from '@/utils/format';
import { useAuthStore } from '@/context/authStore';
import toast from 'react-hot-toast';

const TX_LABELS: Record<string, string> = {
  earn: 'Compra',
  redeem: 'Canje',
  adjust: 'Ajuste manual',
  reverse_earn: 'Reversa (venta cancelada)',
  reverse_redeem: 'Devolución de canje',
};

export default function Loyalty() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const queryClient = useQueryClient();

  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showRegister, setShowRegister] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ['loyalty', 'search', query],
    queryFn: () => loyaltyService.search(query || undefined),
  });

  const { data: detail, isLoading: detailLoading, error: detailError } = useQuery({
    queryKey: ['loyalty', 'customer', selectedId],
    queryFn: () => loyaltyService.getCustomer(selectedId!),
    enabled: selectedId != null,
  });

  // Edición de los datos del cliente (se van completando de a poco)
  const [editando, setEditando] = useState(false);
  const [editNombre, setEditNombre] = useState('');
  const [editTelefono, setEditTelefono] = useState('');

  const cerrarDetalle = () => {
    setSelectedId(null);
    setEditando(false);
  };

  const guardarDatos = useMutation({
    mutationFn: ({ id }: { id: number }) =>
      loyaltyService.update(id, { name: editNombre.trim(), phone: editTelefono.trim() }),
    onSuccess: () => {
      toast.success('Datos guardados');
      setEditando(false);
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'customer', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'search'] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail || 'No se pudieron guardar los datos'),
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-taupe-800 flex items-center gap-2">
          <Star className="h-6 w-6 text-amber-500" /> Clientes VIP
        </h1>
        <p className="text-taupe-500">
          Suman 1 punto por cada $1.000 (mínimo $10.000 por compra). 1.000 puntos = {formatPrice(30000)} de descuento.
        </p>
      </div>

      {/* Buscador + registrar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-taupe-400" />
          <input
            type="text"
            placeholder="Buscar por DNI o nombre…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 border-2 border-cream-200 rounded-xl text-base focus:outline-none focus:border-taupe-400"
          />
        </div>
        <Button onClick={() => setShowRegister(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> Registrar
        </Button>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-2xl border border-cream-200 divide-y divide-cream-100">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-taupe-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12 text-taupe-400">
            <Star className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No hay clientes VIP{query ? ' con ese criterio' : ' todavía'}</p>
          </div>
        ) : (
          customers.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-cream-50 transition-colors"
            >
              <div className="min-w-0">
                <p className="font-medium text-taupe-800 truncate">{c.name || 'Sin nombre'}</p>
                <p className="text-xs text-taupe-500">DNI {c.dni}{c.phone ? ` · ${c.phone}` : ''}</p>
              </div>
              <span className="flex items-center gap-1.5 text-amber-700 font-bold whitespace-nowrap">
                <Star className="h-4 w-4 text-amber-500" /> {c.points_balance} pts
              </span>
            </button>
          ))
        )}
      </div>

      {/* Detalle del cliente */}
      <Modal isOpen={selectedId != null} onClose={() => cerrarDetalle()} title="Cliente VIP">
        {/* Sin estos dos casos el modal quedaba en blanco cuando el pedido
            fallaba, sin ninguna pista de que habia pasado */}
        {detailLoading && (
          <div className="flex items-center justify-center py-10 text-taupe-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
        {detailError && !detailLoading && (
          <div className="py-6 text-center">
            <p className="text-rose-600 font-medium">No se pudo cargar el cliente</p>
            <p className="text-sm text-taupe-500 mt-1">
              {(detailError as any)?.response?.data?.detail || (detailError as any)?.message || 'Error inesperado'}
            </p>
          </div>
        )}
        {detail && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-lg font-bold text-taupe-800">{detail.name || 'Sin nombre'}</p>
                <p className="text-sm text-taupe-500">DNI {detail.dni}{detail.phone ? ` · ${detail.phone}` : ''}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-amber-600">{detail.points_balance}</p>
                <p className="text-xs text-taupe-400 uppercase tracking-wide">puntos</p>
              </div>
            </div>

            {/* Datos del cliente: se completan de a poco (en la venta suele
                cargarse solo el DNI). Se abre solo si todavia falta el nombre. */}
            <div className="rounded-xl border border-cream-200 p-3">
              {!editando ? (
                <button
                  type="button"
                  className="text-sm text-amber-700 hover:text-amber-800 font-medium"
                  onClick={() => {
                    setEditNombre(detail.name || '');
                    setEditTelefono(detail.phone || '');
                    setEditando(true);
                  }}
                >
                  {detail.name ? 'Editar datos' : '+ Completar nombre y teléfono'}
                </button>
              ) : (
                <div className="space-y-3">
                  <Input
                    label="Nombre y apellido"
                    placeholder="Juan Perez"
                    value={editNombre}
                    onChange={(e) => setEditNombre(e.target.value)}
                    autoFocus
                  />
                  <Input
                    label="Teléfono (opcional)"
                    placeholder="381 555 1234"
                    value={editTelefono}
                    onChange={(e) => setEditTelefono(e.target.value)}
                  />
                  <p className="text-xs text-taupe-400">Se guarda en MAYÚSCULA.</p>
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => setEditando(false)}>Cancelar</Button>
                    <Button
                      onClick={() => guardarDatos.mutate({ id: detail.id })}
                      loading={guardarDatos.isPending}
                    >
                      Guardar
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="rounded-xl bg-cream-50 border border-cream-200 p-3">
                <p className="text-lg font-bold text-taupe-700">{detail.points_earned_total}</p>
                <p className="text-xs text-taupe-500">Ganados histórico</p>
              </div>
              <div className="rounded-xl bg-cream-50 border border-cream-200 p-3">
                <p className="text-lg font-bold text-taupe-700">{detail.points_redeemed_total}</p>
                <p className="text-xs text-taupe-500">Canjeados histórico</p>
              </div>
            </div>

            {isAdmin && (
              <Button variant="secondary" className="w-full" onClick={() => setShowAdjust(true)}>
                Ajustar puntos manualmente
              </Button>
            )}

            {/* Historial */}
            <div>
              <p className="text-sm font-semibold text-taupe-700 flex items-center gap-1.5 mb-2">
                <History className="h-4 w-4" /> Historial
              </p>
              <div className="rounded-xl border border-cream-200 divide-y divide-cream-100 max-h-64 overflow-y-auto">
                {detail.transactions.length === 0 ? (
                  <p className="text-sm text-taupe-400 text-center py-4">Sin movimientos</p>
                ) : (
                  detail.transactions.map((t) => (
                    <div key={t.id} className="flex items-center justify-between gap-2 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm text-taupe-700">{TX_LABELS[t.type] || t.type}</p>
                        <p className="text-xs text-taupe-400 truncate">{formatDateTime(t.created_at)}{t.note ? ` · ${t.note}` : ''}</p>
                      </div>
                      <span className={`text-sm font-bold whitespace-nowrap ${t.points >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {t.points >= 0 ? '+' : ''}{t.points}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onDone={() => { setShowRegister(false); queryClient.invalidateQueries({ queryKey: ['loyalty', 'search'] }); }}
        />
      )}

      {showAdjust && detail && (
        <AdjustModal
          customer={detail}
          onClose={() => setShowAdjust(false)}
          onDone={() => {
            setShowAdjust(false);
            queryClient.invalidateQueries({ queryKey: ['loyalty', 'customer', detail.id] });
            queryClient.invalidateQueries({ queryKey: ['loyalty', 'search'] });
          }}
        />
      )}
    </div>
  );
}

function RegisterModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [dni, setDni] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const mut = useMutation({
    mutationFn: () => loyaltyService.register({ dni: dni.trim(), name: name.trim() || undefined, phone: phone.trim() || undefined }),
    onSuccess: () => { toast.success('Cliente VIP registrado'); onDone(); },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo registrar'),
  });
  return (
    <Modal isOpen onClose={onClose} title="Registrar cliente VIP">
      <div className="space-y-3">
        <Input label="DNI" value={dni} onChange={(e) => setDni(e.target.value)} placeholder="Ej: 31254024" autoFocus />
        <Input label="Nombre (opcional)" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Teléfono (opcional)" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <div className="flex gap-2 pt-1">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Cancelar</Button>
          <Button className="flex-1" disabled={!dni.trim() || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? 'Guardando…' : 'Registrar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AdjustModal({ customer, onClose, onDone }: { customer: LoyaltyCustomer; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState('');
  const [sign, setSign] = useState<1 | -1>(1);
  const [note, setNote] = useState('');
  const mut = useMutation({
    mutationFn: () => loyaltyService.adjust(customer.id, sign * (parseInt(amount) || 0), note.trim() || undefined),
    onSuccess: () => { toast.success('Puntos ajustados'); onDone(); },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo ajustar'),
  });
  const n = parseInt(amount) || 0;
  return (
    <Modal isOpen onClose={onClose} title="Ajustar puntos">
      <div className="space-y-3">
        <p className="text-sm text-taupe-500">Saldo actual: <strong>{customer.points_balance} pts</strong></p>
        <div className="flex gap-2">
          <button
            onClick={() => setSign(1)}
            className={`flex-1 py-2 rounded-lg border font-medium flex items-center justify-center gap-1 ${sign === 1 ? 'bg-emerald-100 border-emerald-400 text-emerald-700' : 'bg-white border-cream-200 text-taupe-500'}`}
          ><Plus className="h-4 w-4" /> Sumar</button>
          <button
            onClick={() => setSign(-1)}
            className={`flex-1 py-2 rounded-lg border font-medium flex items-center justify-center gap-1 ${sign === -1 ? 'bg-rose-100 border-rose-400 text-rose-700' : 'bg-white border-cream-200 text-taupe-500'}`}
          ><Minus className="h-4 w-4" /> Restar</button>
        </div>
        <Input label="Cantidad de puntos" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        <Input label="Motivo (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
        {n > 0 && (
          <p className="text-sm text-taupe-600">
            Nuevo saldo: <strong>{Math.max(0, customer.points_balance + sign * n)} pts</strong>
          </p>
        )}
        <div className="flex gap-2 pt-1">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Cancelar</Button>
          <Button className="flex-1" disabled={n <= 0 || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? 'Guardando…' : 'Aplicar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
